import {
  LedPanelData,
  LedPanelMetaData,
  offStateDefaultValues,
  onStateDefaultValues,
} from "./PanelDef";
import React from "react";
import { PanelViewComponent } from "../PanelDef";
import { TableContainer } from "../util";
import { isOversizedIntegerString } from "../../../../../bigIntJson";
import { Table, TableHeaderCell } from "semantic-ui-react";
import { capitalizeFirstLetter, copyContentToClipboard } from "../../../util";
interface LedProps {
  size: number;
  color: string;
}

const LedBulb = (props: LedProps) => (
  <svg height={props.size} width={props.size}>
    <circle
      cx={props.size / 2}
      cy={props.size / 2}
      r={props.size / 2 - 1}
      stroke="black"
      strokeWidth="2"
      fill={props.color}
    />
  </svg>
);

const determineLedColor = (
  value,
  panelMeta,
  booleanCheckTruthyArray,
  booleanCheckFalsyArray
) => {
  if (typeof value === "number" || typeof value === "boolean") {
    return value ? panelMeta.onStateColor : panelMeta.offStateColor;
  }

  // A 64-bit integer carried as an exact digit string (see bigIntJson.ts).
  // Deliberately narrow: only values the pre-pass could have produced, so an
  // existing String column holding "0"/"1" keeps its current behaviour.
  // BigInt(0), never the literal 0n — tsconfig targets es5.
  if (isOversizedIntegerString(value)) {
    return BigInt(value.trim()) !== BigInt(0)
      ? panelMeta.onStateColor
      : panelMeta.offStateColor;
  }

  const stringValue = (
    typeof value === "string" ? value : String(value)
  ).toLowerCase();

  if (
    booleanCheckTruthyArray?.some((text) => text.toLowerCase() === stringValue)
  ) {
    return panelMeta.onStateColor;
  } else if (
    booleanCheckFalsyArray?.some((text) => text.toLowerCase() === stringValue)
  ) {
    return panelMeta.offStateColor;
  }
  return "grey";
};

class LedArray extends PanelViewComponent<LedPanelMetaData, LedPanelData> {
  render() {
    let data = this.props.data.data;
    let size = 30;
    if (this.props.panelMeta?.ledSize) {
      size = this.props.panelMeta.ledSize;
    }
    const onStateTextList = this.props.panelMeta?.onStateTextList ?? [];
    const offStateTextList = this.props.panelMeta?.offStateTextList ?? [];

    let booleanCheckTruthyArray: string[] = [
      ...onStateDefaultValues,
      ...onStateTextList,
    ];

    let booleanCheckFalsyArray: string[] = [
      ...offStateDefaultValues,
      ...offStateTextList,
    ];

    return (
      <TableContainer>
        <div className="tableContentContainer">
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "wrap",
              justifyContent: "space-around",
              overflow: "auto",
            }}
          >
            {data.map((item) => {
              let value = item?.value;
              let columnName = item?.column ? item?.column : "-";
              let ledColor: string = determineLedColor(
                value,
                this.props.panelMeta,
                booleanCheckTruthyArray,
                booleanCheckFalsyArray
              );

              return (
                <div
                  key={columnName}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    margin: "40px 10px 10px 10px",
                  }}
                >
                  <div>
                    <LedBulb size={size} color={ledColor} />
                  </div>
                  {columnName && <div>{columnName}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </TableContainer>
    );
  }
}
class MultiDeviceLedArray extends PanelViewComponent<
  LedPanelMetaData,
  LedPanelData
> {
  render() {
    let data = this.props.data["all-data"];
    const columns = this.props.panelMeta.columns;
    let size = 30;
    if (this.props.panelMeta?.ledSize) {
      size = this.props.panelMeta.ledSize;
    }
    const onStateTextList = this.props.panelMeta?.onStateTextList ?? [];
    const offStateTextList = this.props.panelMeta?.offStateTextList ?? [];

    let booleanCheckTruthyArray: string[] = [
      ...onStateDefaultValues,
      ...onStateTextList,
    ];

    let booleanCheckFalsyArray: string[] = [
      ...offStateDefaultValues,
      ...offStateTextList,
    ];

    return (
      <TableContainer>
        <div className="tableContentContainer">
          <Table
            compact
            selectable
            unstackable
            style={{ minWidth: "230px" }}
            textAlign="center"
          >
            <Table.Header>
              <Table.Row>
                {
                  // Displaying serial metadata key as header if it exists else 'Device ID'
                  data[0]?.["-serial_metadata"] ? (
                    <TableHeaderCell key="serial-metadata">
                      {`#${capitalizeFirstLetter(
                        Object?.keys(data[0]?.["-serial_metadata"]).toString()
                      )}`}
                    </TableHeaderCell>
                  ) : (
                    <TableHeaderCell key="id">Device ID</TableHeaderCell>
                  )
                }
                {columns.map((column, index) => (
                  <TableHeaderCell key={index}>{column.name}</TableHeaderCell>
                ))}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data.map((item, index) => (
                <Table.Row key={index}>
                  <Table.Cell
                    onClick={() =>
                      copyContentToClipboard(
                        item?.["-serial_metadata"]
                          ? item?.["-serial_metadata"]?.[
                              Object.keys(item?.["-serial_metadata"])?.[0]
                            ]
                          : item.id
                      )
                    }
                  >
                    {item?.["-serial_metadata"]
                      ? item?.["-serial_metadata"]?.[
                          Object.keys(item?.["-serial_metadata"])?.[0]
                        ]
                      : item.id}
                  </Table.Cell>
                  {columns.map((column, columnIndex) => {
                    let value = item[column?.name];
                    let ledColor: string = determineLedColor(
                      value,
                      this.props.panelMeta,
                      booleanCheckTruthyArray,
                      booleanCheckFalsyArray
                    );
                    return (
                      <Table.Cell key={columnIndex}>
                        <LedBulb size={size} color={ledColor} />
                      </Table.Cell>
                    );
                  })}
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>
      </TableContainer>
    );
  }
}

export class ViewLedPanel extends PanelViewComponent<
  LedPanelMetaData,
  LedPanelData
> {
  render() {
    const { data } = this.props;
    if (data["all-data"][0]?.id && data["all-data"].length > 0) {
      data["all-data"] = data["all-data"].sort((a, b) => {
        return parseInt(a.id, 10) - parseInt(b.id, 10);
      });
      return <MultiDeviceLedArray {...this.props} />;
    } else if (
      data.data.length > 0 &&
      data?.data?.[0]?.["value"] !== undefined
    ) {
      return <LedArray {...this.props} />;
    } else {
      return <div className="panel-no-data">No Data</div>;
    }
  }
}
